import React, { useState } from 'react';
import styled from 'styled-components';
import { fetchUrlMetadata } from '../../utils/bookmarkUtils'; // Import fetchUrlMetadata
import { RefreshIcon } from '../icons/RefreshIcon'; // Import RefreshIcon
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalTitle,
    CloseButton,
    Form,
    FormGroup,
    Label,
    Input,
    TextArea,
    ButtonGroup,
    CancelButton,
    CreateButton,
    HelperText
} from '../../styles/AddProjectModal.styles';
import CloseIcon from '../icons/CloseIcon';

const ErrorMessage = styled.div`
  color: #ff5630;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const TitleInputContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const InputTitle = styled(Input)`
  width: calc(100% - 30px); /* Adjust width to accommodate the refresh button */
`;

const RefreshButton = styled.button`
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: #B6C2CF;
  padding: 0;
  
  &:hover {
    color: #fff;
  }
`;

const ModalOverlayStyled = styled(ModalOverlay)`
    z-index: 1001; /* Ensure modal is on top */
`;

const EditBookmarkModal = ({ bookmark, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        title: bookmark.title || '',
        url: bookmark.url || '',
        notes: bookmark.notes || ''
    });
    
    const [errors, setErrors] = useState({});
    const [loadingTitle, setLoadingTitle] = useState(false); // Added loadingTitle state

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        
        if (!formData.url.trim()) {
            newErrors.url = 'URL is required';
        } else {
            try {
                new URL(formData.url);
            } catch {
                newErrors.url = 'Please enter a valid URL';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            onSave({
                ...bookmark,
                ...formData,
                updatedAt: new Date().toISOString()
            });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleRefreshTitle = async () => {
        setLoadingTitle(true);
        try {
            const metadata = await fetchUrlMetadata(formData.url);
            setFormData(prev => ({
                ...prev,
                title: metadata.title
            }));
        } catch (error) {
            console.error('Error fetching title from webhook:', error);
            // Optionally display an error message to the user
        } finally {
            setLoadingTitle(false);
        }
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>Edit Bookmark</ModalTitle>
                    <CloseButton onClick={onClose}>
                        <CloseIcon />
                    </CloseButton>
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="title">Title</Label>
                        <TitleInputContainer>
                            <InputTitle
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                error={errors.title}
                                placeholder="Enter bookmark title"
                                autoFocus
                            />
                            <RefreshButton type="button" onClick={handleRefreshTitle} disabled={loadingTitle} aria-label="Refresh title">
                                <RefreshIcon />
                            </RefreshButton>
                        </TitleInputContainer>
                        {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
                    </FormGroup>
                    
                    <FormGroup>
                        <Label htmlFor="url">URL</Label>
                        <Input
                            id="url"
                            name="url"
                            value={formData.url}
                            onChange={handleChange}
                            error={errors.url}
                            placeholder="Enter bookmark URL"
                        />
                        {errors.url && <ErrorMessage>{errors.url}</ErrorMessage>}
                    </FormGroup>
                    
                    <FormGroup>
                        <Label htmlFor="notes">Notes (Optional)</Label>
                        <TextArea
                            id="notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            placeholder="Add notes about this bookmark"
                        />
                    </FormGroup>
                    
                    <ButtonGroup>
                        <CancelButton onClick={onClose}>
                            Cancel
                        </CancelButton>
                        <CreateButton type="submit">
                            Save Changes
                        </CreateButton>
                    </ButtonGroup>
                </Form>
            </ModalContent>
        </ModalOverlay>
    );
};

export default EditBookmarkModal;